import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Paperclip, X, FileText, FileImage, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function iconOf(mime) {
    if (mime?.startsWith("image/")) return FileImage;
    if (mime?.includes("sheet") || mime?.includes("excel") || mime?.includes("csv")) return FileSpreadsheet;
    return FileText;
}

function fmtSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export default function AttachmentUploader({ value = [], onChange }) {
    const inp = useRef(null);

    const pick = () => inp.current?.click();

    const handleFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        const newItems = [...value];
        for (const f of files) {
            try {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/uploads", fd);
                newItems.push(data);
            } catch (err) { toast.error(formatApiError(err, `فشل رفع ${f.name}`)); }
        }
        onChange(newItems);
        if (inp.current) inp.current.value = "";
    };

    const remove = (id) => onChange(value.filter((x) => x.id !== id));

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={pick} data-testid="btn-attach"><Paperclip className="w-4 h-4 ml-1" /> إرفاق ملف</Button>
                <div className="text-xs text-slate-400">PDF / صور / Word / PowerPoint / Excel — حد 25MB</div>
                <input ref={inp} type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt" onChange={handleFiles} data-testid="attach-input" />
            </div>
            {value.length > 0 && (
                <div className="space-y-2">
                    {value.map((a) => {
                        const Icon = iconOf(a.mime);
                        const isImg = a.mime?.startsWith("image/");
                        return (
                            <div key={a.id} className="border border-slate-200 rounded-lg p-3 flex items-center gap-3">
                                {isImg ? (
                                    <img src={`${BACKEND}${a.url}`} alt={a.filename} className="w-12 h-12 rounded object-cover" />
                                ) : (
                                    <div className="w-12 h-12 rounded bg-slate-100 text-[#1B2A4E] flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm truncate">{a.filename}</div>
                                    <div className="text-xs text-slate-500 font-mono-num" dir="ltr">{fmtSize(a.size)} • {a.mime}</div>
                                </div>
                                <Button type="button" variant="ghost" size="icon" onClick={() => remove(a.id)} data-testid={`btn-remove-${a.id}`} className="text-[#C8102E]"><X className="w-4 h-4" /></Button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
